/**
 * «Редактор системы» — экран AK-773 по утверждённому макету
 * `docs/superpowers/prototypes/season-planning-2027/07-system-editor.html`.
 *
 * Устройство макета 1:1: шапка (тип · культура · расчётная урожайность ·
 * статус), таблица обработок в порядке фенофаз, итоги под ней, справа — «Где
 * используется» и действия.
 *
 * Всё, что здесь заведено, — НА 1 ГЕКТАР; окна задаются по фенофазам, точные
 * даты подставит сезон той техкарты, куда система вставлена. Это и написано на
 * экране: иначе агроном решит, что видит календарь конкретного поля.
 *
 * Диалоги строки и препаратов ПЕРЕИСПОЛЬЗОВАНЫ с экрана техкарт: строка
 * системы — это строка техкарты (одно и то же тело API), и вторая форма «того
 * же самого» разъехалась бы с оригиналом на первой же правке.
 */
import { useMemo, useState } from 'react';
import { useNavigate, useParams } from '@tanstack/react-router';
import { AppShell } from '@/components/layout/AppShell';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { PageWithRail } from '@/components/ui/page-with-rail';
import { Skeleton } from '@/components/ui/skeleton';
import { Tile, TileGrid } from '@/components/ui/tile-grid';
import { useCan } from '@/lib/auth/usePermissions';
import { toast } from '@/lib/toast';
import {
  formatPerHa,
  techCardErrorText,
  useCropNames,
  type BlockKind,
  type TechCardOperation,
} from '@/lib/planning/techCards';
import {
  MODULE_KIND_TITLES,
  MODULE_STATUS_TITLES,
  formatYieldRange,
  useAddModuleOperation,
  useDeleteModuleOperation,
  usePatchModuleOperation,
  usePatchSystemModule,
  useSetModuleMaterials,
  useSystemModule,
  type ModuleOperation,
} from '@/lib/planning/techCardModules';
import { MaterialsDialog } from '../tech-cards/MaterialsDialog';
import { OperationDialog, type OperationDialogSubmit } from '../tech-cards/OperationDialog';
import { SystemOperationsTable } from './SystemOperationsTable';

export function SystemEditorPage() {
  const params = useParams({ strict: false }) as { moduleId?: string };
  const moduleId = params.moduleId ?? null;
  const navigate = useNavigate();

  const canRead = useCan('tech_cards', 'r');
  const canWrite = useCan('tech_cards', 'u');

  const detail = useSystemModule(canRead ? moduleId : null);
  // Ответ карточки ПЛОСКИЙ: система и её состав в одном объекте.
  const module = detail.data ?? null;
  const cropNames = useCropNames([module?.crop_id]);

  const patchModule = usePatchSystemModule();
  const addOperation = useAddModuleOperation(moduleId);
  const patchOperation = usePatchModuleOperation(moduleId);
  const deleteOperation = useDeleteModuleOperation(moduleId);
  const setMaterials = useSetModuleMaterials(moduleId);

  const [editOp, setEditOp] = useState<{ operation: ModuleOperation | null } | null>(null);
  const [materialsFor, setMaterialsFor] = useState<ModuleOperation | null>(null);
  const [deleteFor, setDeleteFor] = useState<ModuleOperation | null>(null);

  // Материалы приезжают отдельной картой (ключ — строка); диалоги техкарт
  // читают их из самой строки, поэтому сшиваем здесь, а не в каждом месте.
  const operations: ModuleOperation[] = useMemo(() => {
    const byOp = detail.data?.materials ?? {};
    return (detail.data?.operations ?? []).map((op) => ({
      ...op,
      materials: byOp[op.id] ?? op.materials ?? [],
    }));
  }, [detail.data]);

  const cost = detail.data?.cost ?? null;
  const usedIn = detail.data?.used_in ?? [];

  return (
    <AppShell>
      <div className="mx-auto w-full max-w-[1600px] pb-safe-fab">
        <PageHeader
          title={module ? `${MODULE_KIND_TITLES[module.kind]}: «${module.name}»` : 'Система'}
          showBack
          breadcrumbs={[
            { label: 'Планирование сезона', to: '/planning' },
            { label: 'Библиотека систем', to: '/planning/systems' },
            { label: module?.name ?? 'Система' },
          ]}
          description="Мини-техкарта: живёт отдельно, вставляется в любую техкарту, заменяется целиком. Все объёмы — на 1 га; окна задаются по фенофазам, даты подставит сезон техкарты."
        />

        {!canRead && (
          <Card className="mt-4">
            <CardContent className="p-4 text-sm text-muted-foreground">
              Системы вам не открыты. Доступ даёт администратор хозяйства — попросите роль с
              правом на техкарты.
            </CardContent>
          </Card>
        )}

        {canRead && detail.isLoading && <Skeleton className="mt-4 h-96 w-full" />}

        {canRead && detail.isError && (
          <Card className="mt-4">
            <CardContent className="p-4 text-sm text-destructive">
              {techCardErrorText(detail.error)}
            </CardContent>
          </Card>
        )}

        {canRead && module && (
          <div className="mt-4">
            <PageWithRail
              rail={
                <>
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-foreground">Где используется</h3>
                      {usedIn.length === 0 ? (
                        <p className="mt-2 text-sm text-muted-foreground">
                          Система пока ни в одной техкарте. Вставить её можно из карточки
                          техкарты — блок «Системы».
                        </p>
                      ) : (
                        <ul className="mt-2 space-y-1 text-sm">
                          {usedIn.map((u) => (
                            <li key={u.tech_card_id} className="text-foreground">
                              {u.tech_card_name}
                              {u.farm_name ? (
                                <span className="text-muted-foreground"> · {u.farm_name}</span>
                              ) : null}
                            </li>
                          ))}
                        </ul>
                      )}
                      <p className="mt-2 text-xs text-muted-foreground">
                        Замена системы в карте пересчитает её себестоимость.
                      </p>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-sm font-semibold text-foreground">Действия</h3>
                      {canWrite ? (
                        <div className="mt-2 flex flex-wrap gap-2">
                          {module.status === 'ready' ? (
                            <Button
                              size="sm"
                              variant="outline"
                              disabled={patchModule.isPending}
                              onClick={() =>
                                patchModule.mutate(
                                  { moduleId: module.id, patch: { status: 'draft' } },
                                  { onSuccess: () => toast.success('Система снова черновик') },
                                )
                              }
                            >
                              Вернуть в черновик
                            </Button>
                          ) : (
                            <Button
                              size="sm"
                              disabled={patchModule.isPending}
                              onClick={() =>
                                patchModule.mutate(
                                  { moduleId: module.id, patch: { status: 'ready' } },
                                  { onSuccess: () => toast.success('Система утверждена') },
                                )
                              }
                            >
                              Утвердить
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => void navigate({ to: '/planning/systems' })}
                          >
                            Ко всем системам
                          </Button>
                        </div>
                      ) : (
                        <p className="mt-2 text-sm text-muted-foreground">
                          Править систему может пользователь с правом на техкарты.
                        </p>
                      )}
                    </CardContent>
                  </Card>
                </>
              }
            >
              <Card>
                <CardContent className="p-4">
                  <TileGrid>
                    <Tile label="Тип системы" value={MODULE_KIND_TITLES[module.kind]} />
                    <Tile
                      label="Культура"
                      value={
                        module.crop_id
                          ? (cropNames.names[module.crop_id] ??
                            'культура без названия в справочнике')
                          : 'любая культура'
                      }
                    />
                    <Tile label="Рассчитана на урожайность" value={formatYieldRange(module)} />
                    <Tile
                      label="Статус"
                      value={MODULE_STATUS_TITLES[module.status] ?? 'Черновик'}
                    />
                  </TileGrid>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="text-base font-semibold text-foreground">
                      Обработки — в порядке фенофаз культуры
                    </h3>
                    {canWrite && (
                      <Button
                        size="sm"
                        className="ml-auto"
                        onClick={() => setEditOp({ operation: null })}
                      >
                        Добавить обработку
                      </Button>
                    )}
                  </div>
                  <p className="mt-1 max-w-3xl text-sm text-muted-foreground">
                    Строка читается как фраза: против чего → чем → когда. Препараты берутся из
                    бюджетного справочника сезона — позиция без плановой цены в стоимость не
                    попадёт.
                  </p>
                  <div className="mt-3">
                    <SystemOperationsTable
                      kind={module.kind}
                      operations={operations}
                      materials={detail.data?.materials ?? {}}
                      cost={cost}
                      canWrite={canWrite}
                      actions={{
                        onEdit: (op) => setEditOp({ operation: op }),
                        onEditMaterials: setMaterialsFor,
                        onDelete: setDeleteFor,
                      }}
                    />
                  </div>
                </CardContent>
              </Card>

              <Card>
                <CardContent className="p-4">
                  <h3 className="text-base font-semibold text-foreground">Итоги системы</h3>
                  <div className="mt-3">
                    <TileGrid>
                      <Tile
                        label="Стоимость системы"
                        value={formatPerHa(cost?.per_ha ?? null)}
                        size="lg"
                      />
                      <Tile
                        label="Обработок"
                        value={String(operations.length)}
                        size="lg"
                      />
                    </TileGrid>
                  </div>
                  {(cost?.gaps?.length ?? 0) > 0 && (
                    <p className="mt-3 text-sm text-muted-foreground">
                      Посчитать удалось не всё: {(cost?.gaps ?? []).slice(0, 3).join('; ')}
                      {(cost?.gaps.length ?? 0) > 3 ? ' …' : ''}
                    </p>
                  )}
                </CardContent>
              </Card>
            </PageWithRail>
          </div>
        )}
      </div>

      {editOp && module && (
        <OperationDialog
          operation={editOp.operation as TechCardOperation | null}
          blockKind={module.kind as BlockKind}
          seasonId={module.season_id ?? null}
          parentCandidates={operations.filter((op) => !op.parent_operation_id)}
          saving={addOperation.isPending || patchOperation.isPending}
          onSubmit={(values: OperationDialogSubmit) => {
            const done = () => {
              setEditOp(null);
              addOperation.reset();
              patchOperation.reset();
            };
            if (values.create) {
              addOperation.mutate(values.create, {
                onSuccess: () => {
                  done();
                  toast.success('Обработка добавлена — стоимость системы пересчитана');
                },
              });
            } else if (values.patch && editOp.operation) {
              patchOperation.mutate(
                { opId: editOp.operation.id, patch: values.patch },
                {
                  onSuccess: () => {
                    done();
                    toast.success('Обработка сохранена — стоимость системы пересчитана');
                  },
                },
              );
            }
          }}
          onClose={() => {
            setEditOp(null);
            addOperation.reset();
            patchOperation.reset();
          }}
        />
      )}

      {materialsFor && module?.season_id && (
        <MaterialsDialog
          operation={materialsFor}
          seasonId={module.season_id}
          cropId={module.crop_id}
          saving={setMaterials.isPending}
          onSubmit={(materials) =>
            setMaterials.mutate(
              { opId: materialsFor.id, materials },
              {
                onSuccess: () => {
                  setMaterialsFor(null);
                  setMaterials.reset();
                  toast.success('Препараты сохранены — стоимость системы пересчитана');
                },
              },
            )
          }
          onClose={() => {
            setMaterialsFor(null);
            setMaterials.reset();
          }}
        />
      )}

      {deleteFor && (
        <Dialog open onOpenChange={(o) => !o && setDeleteFor(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>
                Убрать обработку «{deleteFor.op_type_name ?? deleteFor.name ?? 'без названия'}»?
              </DialogTitle>
              <DialogDescription>
                Вместе с обработкой уйдут её препараты. Техкарты, куда система уже вставлена,
                пересчитаются при следующей замене системы.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setDeleteFor(null)}
                disabled={deleteOperation.isPending}
              >
                Оставить
              </Button>
              <Button
                variant="destructive"
                disabled={deleteOperation.isPending}
                onClick={() =>
                  deleteOperation.mutate(
                    { opId: deleteFor.id },
                    {
                      onSuccess: () => {
                        setDeleteFor(null);
                        toast.success('Обработка убрана — стоимость системы пересчитана');
                      },
                    },
                  )
                }
              >
                Убрать обработку
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </AppShell>
  );
}
